import { useCallback } from "react";
import useHttp from "./useHttp";

export default function useNotes(appProperties) {
  const { fetchData } = useHttp();

  const fetchNoteById = useCallback(
    (noteId) => {
      return new Promise((resolve, reject) => {
        if (!appProperties?.osyncId) {
          reject(new Error("OsyncId is required"));
          return;
        }

        if (!noteId) {
          reject(new Error("NoteId is required"));
          return;
        }

        fetchData(`omessage/notes/${noteId}`, "GET", null, appProperties)
          .then((response) => {
            let responseData = "";
            if (response) {
              responseData = JSON.parse(response)?.data;
            }
            if (responseData?.success) {
              resolve(responseData);
            } else {
              reject(new Error(responseData?.data));
            }
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    [fetchData]
  );

  const fetchSavedNotesByEntity = useCallback(
    (entityId, paginationParams = {}) => {
      return new Promise((resolve, reject) => {
        if (!appProperties?.osyncId) {
          reject(new Error("OsyncId is required"));
          return;
        }

        if (!entityId) {
          reject(new Error("EntityId is required"));
          return;
        }

        let url = `omessage/${encodeURIComponent(entityId)}/notes`;

        // Add pagination parameters if provided
        const queryParams = [];

        queryParams.push(`limit=${paginationParams.limit || 10}`);

        if (paginationParams.lastKey) {
          queryParams.push(`lastKey=${encodeURIComponent(paginationParams.lastKey)}`);
        }

        if (paginationParams.lastRange) {
          queryParams.push(`lastRange=${encodeURIComponent(paginationParams.lastRange)}`);
        }

        if (queryParams.length > 0) {
          url += `?${queryParams.join("&")}`;
        }

        fetchData(url, "GET", null, appProperties)
          .then((response) => {
            let responseData = "";
            if (response) {
              responseData = JSON.parse(response);
            }

            // Handle both old and new response formats
            let savedNotes = [];
            let paginationModel = null;

            if (responseData?.data?.data) {
              // New format with pagination
              savedNotes = responseData.data.data || [];
              paginationModel = responseData.data.paginationModel || null;
            } else if (responseData?.data) {
              // Old format without pagination
              savedNotes = responseData.data || [];
            } else {
              savedNotes = [];
            }

            resolve({
              data: savedNotes,
              pagination: paginationModel,
              hasMore: paginationModel?.lastKey ? true : false,
            });
          })
          .catch((error) => {
            console.error("Error fetching notes by entity:", error);
            reject(error);
          });
      });
    },
    [fetchData]
  );

  const addNote = useCallback(
    (entityId, noteContent, integId, onNoteAdded) => {
      return new Promise((resolve, reject) => {
        if (!appProperties?.osyncId) {
          reject(new Error("OsyncId is required"));
          return;
        }

        if (!entityId) {
          reject(new Error("EntityId is required"));
          return;
        }

        if (!noteContent) {
          reject(new Error("Note content is required"));
          return;
        }

        let url = `omessage/${encodeURIComponent(entityId)}/notes`;

        if (integId) {
          url += `?integId=${integId}`;
        }

        fetchData(url, "POST", noteContent, appProperties, { "content-type": "text/plain" })
          .then((response) => {
            let responseData = "";
            if (response) {
              responseData = JSON.parse(response)?.data;
            }

            // Call the callback function if provided to update conversation context
            if (onNoteAdded && typeof onNoteAdded === "function") {
              try {
                onNoteAdded(entityId, responseData?.conversation?.data?.messages);
              } catch (error) {
                console.error("Error in onNoteAdded callback:", error);
              }
            }

            resolve(responseData);
          })
          .catch((error) => {
            console.error("Error adding note:", error);
            reject(error);
          });
      });
    },
    [fetchData]
  );

  const updateNote = useCallback(
    (entityId, noteId, noteContent, onNoteUpdated) => {
      return new Promise((resolve, reject) => {
        if (!appProperties?.osyncId) {
          reject(new Error("OsyncId is required"));
          return;
        }

        if (!noteId) {
          reject(new Error("NoteId is required"));
          return;
        }

        if (!noteContent) {
          reject(new Error("Note content is required"));
          return;
        }

        fetchData(`omessage/notes/${noteId}`, "PUT", noteContent, appProperties, { "content-type": "text/plain" })
          .then((response) => {
            let responseData = "";
            if (response) {
              responseData = JSON.parse(response)?.data;
            }

            // Call the callback function if provided to update conversation context
            if (onNoteUpdated && typeof onNoteUpdated === "function") {
              try {
                onNoteUpdated(entityId, responseData?.conversation?.data?.messages);
              } catch (error) {
                console.error("Error in onNoteUpdated callback:", error);
              }
            }

            resolve(responseData);
          })
          .catch((error) => {
            console.error("Error updating note:", error);
            reject(error);
          });
      });
    },
    [fetchData]
  );

  const deleteNote = useCallback(
    (noteId, entityId, onNoteDeleted) => {
      return new Promise((resolve, reject) => {
        if (!appProperties?.osyncId) {
          reject(new Error("OsyncId is required"));
          return;
        }

        if (!noteId) {
          reject(new Error("NoteId is required"));
          return;
        }

        fetchData(`omessage/notes/${noteId}`, "DELETE", null, appProperties)
          .then((response) => {
            let responseData = "";
            if (response) {
              responseData = JSON.parse(response)?.data;
            }

            if (onNoteDeleted && typeof onNoteDeleted === "function") {
              onNoteDeleted(entityId, responseData?.conversation?.data?.messages);
            }

            resolve(responseData);
          })
          .catch((error) => {
            console.error("Error deleting note:", error);
            reject(error);
          });
      });
    },
    [fetchData]
  );

  const stripHtmlTags = useCallback((html) => html.replace(/<[^>]*>?/g, ""), []);

  return {
    fetchSavedNotesByEntity,
    addNote,
    updateNote,
    deleteNote,
    stripHtmlTags,
    fetchNoteById,
  };
}
